import { useState, useCallback, useMemo } from 'react';
import type { AbstractSingleFilterDropdownProps } from '../../SingleFilterType';
import { GenericFilterHeader } from '../GenericFilter/GenericFilterHeader';
import { GenericFilterBody } from '../GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericFilter/GenericFilterFooter';
import type { DropdownStyle, RadioTextFilterValueType } from './GenericRadioTextType';
import { ValidationErrorEnum, RadioTextFilterOperation } from './GenericRadioTextType';
import { GenericRadioTextBody } from './GenericRadioTextBody';
import { GenericSelectTextBody } from './GenericSelectTextBody';
import type { InfoFooter } from '../GenericFilterSelect/GenericFilterSelectType';

interface GenericRadioDropdownProps<T> extends AbstractSingleFilterDropdownProps<T> {
  options: RadioTextFilterOperation[];
  placeholders?: Partial<Record<RadioTextFilterOperation, string>>;
  propertyName?: string;
  dropdownStyle?: DropdownStyle;
  infoFooter?: InfoFooter;
}

function getStringValue(value?: RadioTextFilterValueType): string {
  if (!value) {
    return '';
  }
  if ('value' in value) {
    return value.value;
  }
  return '';
}

function getFilterValueErrors(
  operation?: RadioTextFilterOperation,
  value?: string,
): ValidationErrorEnum | undefined {
  if (!operation) {
    return ValidationErrorEnum.SELECT_ONE_OPTION;
  }
  if (
    operation !== RadioTextFilterOperation.IS_UNKNOWN &&
    operation !== RadioTextFilterOperation.HAS_ANY_VALUE &&
    (value == null || value === '')
  ) {
    return ValidationErrorEnum.TEXT_EMPTY;
  }
  if (
    (operation === RadioTextFilterOperation.CONTAINS ||
      operation === RadioTextFilterOperation.NOT_CONTAINS) &&
    (value == null || value.length < 3)
  ) {
    return ValidationErrorEnum.TEXT_TOO_SHORT;
  }
  return undefined;
}

const DROPDOWN_WIDTH_WITH_SELECT = 354;

export function GenericRadioTextDropdown({
  currentValue,
  optionalDefaultValue,
  onApply,
  options = [],
  filterName,
  placeholders,
  header,
  propertyName,
  dropdownStyle,
  infoFooter,
}: GenericRadioDropdownProps<RadioTextFilterValueType>): JSX.Element {
  const initialValue = useMemo(
    () => currentValue ?? optionalDefaultValue,
    [currentValue, optionalDefaultValue],
  );
  const [dirtyOperation, setDirtyOperation] = useState<RadioTextFilterOperation | undefined>(
    initialValue?.operation ?? options.find(() => true),
  );
  const [dirtyStringValue, setDirtyStringValue] = useState(getStringValue(initialValue));

  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const onOperationChange = useCallback(
    (newOperation: RadioTextFilterOperation) => {
      setDirtyOperation(newOperation);
      setValidationError(undefined);
    },
    [setDirtyOperation, setValidationError],
  );

  const onFilterTextChange = useCallback(
    (newValue: string) => {
      setDirtyStringValue(newValue);
      setValidationError(undefined);
    },
    [setDirtyStringValue, setValidationError],
  );

  const handleApply = useCallback(() => {
    const trimmedDirtyValue = dirtyStringValue?.trim();
    const filterValueErrors = getFilterValueErrors(dirtyOperation, trimmedDirtyValue);
    if (filterValueErrors) {
      setValidationError(filterValueErrors);
      setDirtyStringValue(trimmedDirtyValue);
    } else if (!dirtyOperation) {
      onApply(undefined);
    } else if (
      dirtyOperation === RadioTextFilterOperation.IS_UNKNOWN ||
      dirtyOperation === RadioTextFilterOperation.HAS_ANY_VALUE
    ) {
      onApply({
        operation: dirtyOperation,
      });
    } else {
      onApply({
        operation: dirtyOperation,
        value: trimmedDirtyValue,
      });
    }
  }, [onApply, dirtyOperation, dirtyStringValue]);

  return (
    <GenericFilterDropdown
      ariaLabel={`${filterName} dropdown`}
      onApply={handleApply}
      customWidth={dropdownStyle === 'withSelect' ? DROPDOWN_WIDTH_WITH_SELECT : undefined}
    >
      {header != null && <GenericFilterHeader>{header}</GenericFilterHeader>}
      <GenericFilterBody>
        {dropdownStyle === 'withSelect' ? (
          <GenericSelectTextBody
            operation={dirtyOperation}
            onOperationChange={onOperationChange}
            stringValue={dirtyStringValue}
            onStringChange={onFilterTextChange}
            operationOptions={options}
            placeholders={placeholders}
            validationError={validationError}
            propertyName={propertyName}
            infoFooter={infoFooter}
          />
        ) : (
          <GenericRadioTextBody
            operation={dirtyOperation}
            onOperationChange={onOperationChange}
            stringValue={dirtyStringValue}
            onStringChange={onFilterTextChange}
            operationOptions={options}
            filterName={filterName}
            placeholders={placeholders}
            validationError={validationError}
          />
        )}
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
